// <styled-select> — light-DOM enhancement of a server-rendered native <select>.
// The native select stays in the DOM (inside a hidden fallback wrapper) as the
// form-participating value holder and the no-JS fallback; this element builds
// the trigger + listbox UI around it. Idempotent under Turbo Drive restores.

const TRIGGER_CLASSES =
	"flex w-full items-center justify-between gap-2 rounded-lg border border-edge bg-black/30 " +
	"px-3.5 py-2.5 text-left text-sm text-white focus:border-accent/60 focus:outline-none";
const PANEL_CLASSES =
	"absolute z-50 mt-1.5 max-h-64 w-full overflow-y-auto rounded-lg border border-edge " +
	"bg-panel p-1 shadow-xl";
const OPTION_CLASSES =
	"flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-2 text-sm text-white/80";
const OPTION_ACTIVE = ["bg-accent", "text-black"];

const CHEVRONS_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="size-3.5 shrink-0 text-white/40">' +
	'<path d="M8 9l4-4 4 4M8 15l4 4 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHECK_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-3.5 shrink-0">' +
	'<path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class StyledSelect extends HTMLElement {
	#select: HTMLSelectElement | null = null;
	#trigger: HTMLButtonElement | null = null;
	#labelEl: HTMLSpanElement | null = null;
	#panel: HTMLUListElement | null = null;
	#options: HTMLLIElement[] = [];
	#active = -1;
	#abort: AbortController | null = null;
	#typeahead = "";
	#typeaheadTimer: number | undefined;

	connectedCallback() {
		const select = this.querySelector("select");
		if (!select) return;
		this.#select = select;
		if (!this.querySelector("[data-styled-select-trigger]"))
			this.#build(select);
		this.#wire();
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	#build(select: HTMLSelectElement) {
		this.classList.add("relative", "block");

		// Hide the entire server-rendered fallback (select + its chevron); the
		// select inside still participates in form submission.
		const fallback = this.querySelector<HTMLElement>(
			"[data-styled-select-fallback]",
		);
		if (fallback) fallback.hidden = true;
		select.tabIndex = -1;
		select.setAttribute("aria-hidden", "true");

		const trigger = document.createElement("button");
		trigger.type = "button";
		trigger.className = TRIGGER_CLASSES;
		trigger.dataset.styledSelectTrigger = "";
		trigger.setAttribute("aria-haspopup", "listbox");
		trigger.setAttribute("aria-expanded", "false");
		const label = document.createElement("span");
		label.className = "truncate";
		trigger.append(label);
		trigger.insertAdjacentHTML("beforeend", CHEVRONS_SVG);

		const panel = document.createElement("ul");
		panel.className = PANEL_CLASSES;
		panel.dataset.styledSelectPanel = "";
		panel.setAttribute("role", "listbox");
		panel.hidden = true;

		for (const option of Array.from(select.options)) {
			const li = document.createElement("li");
			li.className = OPTION_CLASSES;
			li.dataset.value = option.value;
			li.setAttribute("role", "option");
			const check = document.createElement("span");
			check.className = "w-3.5 shrink-0";
			check.innerHTML = CHECK_SVG;
			const text = document.createElement("span");
			text.className = "truncate";
			text.textContent = option.textContent;
			li.append(check, text);
			panel.append(li);
		}

		this.append(trigger, panel);
	}

	#wire() {
		const trigger = this.querySelector<HTMLButtonElement>(
			"[data-styled-select-trigger]",
		);
		const panel = this.querySelector<HTMLUListElement>(
			"[data-styled-select-panel]",
		);
		const label = trigger?.querySelector("span");
		if (!trigger || !panel || !label) return;

		this.#trigger = trigger;
		this.#panel = panel;
		this.#labelEl = label;
		this.#options = Array.from(panel.querySelectorAll("li"));
		this.#sync();

		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		// External value changes (e.g. the prefill_guesses stream action)
		// dispatch change on the native select — mirror them into the UI.
		this.#select?.addEventListener("change", () => this.#sync(), { signal });

		trigger.addEventListener("click", () => this.#toggle(), { signal });
		trigger.addEventListener("keydown", (event) => this.#keydown(event), {
			signal,
		});
		panel.addEventListener(
			"click",
			(event) => {
				const li = (event.target as HTMLElement).closest("li");
				if (li) this.#choose(this.#options.indexOf(li));
			},
			{ signal },
		);
		panel.addEventListener(
			"mousemove",
			(event) => {
				const li = (event.target as HTMLElement).closest("li");
				if (li) this.#setActive(this.#options.indexOf(li));
			},
			{ signal },
		);
		document.addEventListener(
			"click",
			(event) => {
				if (!this.contains(event.target as Node)) this.#close();
			},
			{ signal },
		);
	}

	get #open() {
		return this.#panel ? !this.#panel.hidden : false;
	}

	#toggle() {
		if (this.#open) {
			this.#close();
		} else {
			this.#openPanel();
		}
	}

	#openPanel() {
		if (!this.#panel || !this.#trigger || !this.#select) return;
		this.#panel.hidden = false;
		this.#trigger.setAttribute("aria-expanded", "true");
		this.#setActive(this.#select.selectedIndex);
	}

	#close() {
		if (!this.#open || !this.#panel || !this.#trigger) return;
		this.#panel.hidden = true;
		this.#trigger.setAttribute("aria-expanded", "false");
	}

	#keydown(event: KeyboardEvent) {
		const { key } = event;
		if (!this.#open) {
			if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(key)) {
				event.preventDefault();
				this.#openPanel();
			}
			return;
		}

		if (key === "ArrowDown") {
			event.preventDefault();
			this.#setActive(this.#active + 1);
		} else if (key === "ArrowUp") {
			event.preventDefault();
			this.#setActive(this.#active - 1);
		} else if (key === "Home") {
			event.preventDefault();
			this.#setActive(0);
		} else if (key === "End") {
			event.preventDefault();
			this.#setActive(this.#options.length - 1);
		} else if (key === "Enter" || key === " ") {
			event.preventDefault();
			this.#choose(this.#active);
		} else if (key === "Escape" || key === "Tab") {
			this.#close();
		} else if (key.length === 1 && !event.metaKey && !event.ctrlKey) {
			this.#typeaheadFind(key);
		}
	}

	#typeaheadFind(char: string) {
		window.clearTimeout(this.#typeaheadTimer);
		this.#typeahead += char.toLowerCase();
		this.#typeaheadTimer = window.setTimeout(() => {
			this.#typeahead = "";
		}, 500);
		const index = this.#options.findIndex((li) =>
			(li.textContent ?? "").trim().toLowerCase().startsWith(this.#typeahead),
		);
		if (index >= 0) this.#setActive(index);
	}

	#setActive(index: number) {
		this.#active = Math.max(0, Math.min(index, this.#options.length - 1));
		for (const [i, li] of this.#options.entries()) {
			for (const cls of OPTION_ACTIVE) {
				li.classList.toggle(cls, i === this.#active);
			}
		}
		this.#options[this.#active]?.scrollIntoView({ block: "nearest" });
	}

	#choose(index: number) {
		const li = this.#options[index];
		const select = this.#select;
		if (!li || !select) return;
		select.value = li.dataset.value ?? "";
		select.dispatchEvent(new Event("change", { bubbles: true }));
		this.#sync();
		this.#close();
		this.#trigger?.focus();
	}

	#sync() {
		const select = this.#select;
		const label = this.#labelEl;
		if (!select || !label) return;
		const selected = select.options[select.selectedIndex];
		label.textContent = selected?.textContent ?? "";
		for (const li of this.#options) {
			const isSelected = li.dataset.value === select.value;
			li.setAttribute("aria-selected", String(isSelected));
			const check = li.querySelector("span");
			if (check) check.style.visibility = isSelected ? "visible" : "hidden";
		}
	}
}

if (!customElements.get("styled-select")) {
	customElements.define("styled-select", StyledSelect);
}
